/**
 * API client for export job management
 */

import { getKeycloak } from './keycloak';

export interface JobLogEntry {
  timestamp: number;
  message: string;
  level: 'info' | 'success' | 'error' | 'warning';
}

export interface JobProgress {
  total: number;
  current: number;
  currentItem?: string;
  phase?: 'preparing' | 'exporting' | 'uploading' | 'complete';
  bytesDownloaded?: number;
  bytesToDownload?: number;
  bytesUploaded?: number;
  bytesToUpload?: number;
  message?: string;
}

export interface ExportResult {
  success: boolean;
  exported: string[];
  failed: string[];
  message: string;
}

export interface ExportJob {
  id: string;
  type: 'courses' | 'competency_frameworks' | 'learning_path_templates';
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  sourceId: string;
  sourceName: string;
  targetEnvironment: string;
  itemIds: number[];
  startTime: number;
  endTime?: number;
  notificationSeen?: boolean;
  logs?: JobLogEntry[];
  progress: JobProgress;
  result?: ExportResult;
  error?: string;
}

export interface JobStats {
  running: number;
  completed: number;
  failed: number;
  cancelled: number;
  total: number;
}

const API_BASE = '/api';

/**
 * Get authentication headers
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    return {
      'Authorization': `Bearer ${windowToken}`,
      'Content-Type': 'application/json',
    };
  }

  // Fallback to Keycloak instance
  try {
    const keycloak = getKeycloak();
    if (keycloak?.token) {
      return {
        'Authorization': `Bearer ${keycloak.token}`,
        'Content-Type': 'application/json',
      };
    }
  } catch (error) {
    // Keycloak not initialized
  }

  throw new Error('Not authenticated');
}

export class JobsAPI {
  /**
   * Get all jobs (running + history)
   */
  static async getAllJobs(): Promise<ExportJob[]> {
    const response = await fetch(`${API_BASE}/export-jobs`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch jobs');
    }
    return response.json();
  }

  /**
   * Get only running jobs
   */
  static async getRunningJobs(): Promise<ExportJob[]> {
    const response = await fetch(`${API_BASE}/export-jobs/running`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch running jobs');
    }
    return response.json();
  }

  /**
   * Get job history
   */
  static async getHistory(limit?: number): Promise<ExportJob[]> {
    const url = limit
      ? `${API_BASE}/export-jobs/history?limit=${limit}`
      : `${API_BASE}/export-jobs/history`;

    const response = await fetch(url, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch job history');
    }
    return response.json();
  }

  /**
   * Get job statistics
   */
  static async getStats(): Promise<JobStats> {
    const response = await fetch(`${API_BASE}/export-jobs/stats`, {
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch job stats');
    }
    return response.json();
  }

  /**
   * Get specific job by ID
   */
  static async getJob(jobId: string): Promise<ExportJob | null> {
    const response = await fetch(`${API_BASE}/export-jobs/${jobId}`, {
      headers: await getAuthHeaders(),
    });
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch job');
    }
    return response.json();
  }

  /**
   * Request job cancellation
   */
  static async cancelJob(jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/export-jobs/${jobId}/cancel`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to cancel job');
    }
  }

  /**
   * Mark job notification as seen
   */
  static async markJobSeen(jobId: string): Promise<void> {
    const response = await fetch(`${API_BASE}/export-jobs/${jobId}/mark-seen`, {
      method: 'POST',
      headers: await getAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error('Failed to mark job as seen');
    }
  }

  /**
   * Format bytes to human-readable string
   */
  static formatBytes(bytes?: number): string {
    if (bytes === undefined || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  /**
   * Calculate progress percentage
   */
  static getProgressPercentage(progress: JobProgress): number {
    // Use byte progress if available (more accurate for large files)
    if (progress.bytesToDownload && progress.bytesDownloaded !== undefined) {
      return Math.round((progress.bytesDownloaded / progress.bytesToDownload) * 100);
    }
    
    // Fall back to item count
    if (progress.total > 0) {
      return Math.round((progress.current / progress.total) * 100);
    }
    
    return 0;
  }

  /**
   * Get human-readable job type name
   */
  static getJobTypeName(type: ExportJob['type']): string {
    switch (type) {
      case 'courses':
        return 'Courses';
      case 'competency_frameworks':
        return 'Competency Frameworks';
      case 'learning_path_templates':
        return 'Learning Path Templates';
      default:
        return type;
    }
  }

  /**
   * Format duration from milliseconds
   */
  static formatDuration(startTime: number, endTime?: number): string {
    const duration = (endTime || Date.now()) - startTime;
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }
}
